import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Plug, PlugZap, CheckCircle2, RefreshCw, Loader2, AlertTriangle,
  Truck, KeyRound, Settings, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ADMIN ONLY — SelleGard carrier configuration.
 * Manages the site-wide carrier credentials that ALL merchants inherit.
 * Merchants never see this page.
 */
export default function AdminSelleGardConfigPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  const [customerId, setCustomerId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/webship/status`, { headers: auth });
      const s = await r.json();
      setStatus(s);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const doConnect = async () => {
    setBusy('connect'); setBanner(null);
    try {
      const r = await fetch(`${API}/webship/connect`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          customer_id: customerId.trim(),
          api_key: apiKey.trim(),
          integration_id: integrationId.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Connection failed');
      setBanner({ type: 'ok', text: `Connected. ${d.services_count} carrier services live for all merchants.` });
      setCustomerId(''); setApiKey(''); setIntegrationId('');
      await refresh();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const doTest = async () => {
    setBusy('test'); setBanner(null);
    try {
      const r = await fetch(`${API}/webship/test`, { headers: auth });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Test failed');
      setBanner({ type: 'ok', text: `Live: ${d.services_count} shipping services · last checked ${new Date(d.tested_at).toLocaleTimeString()}.` });
      await refresh();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  const doDisconnect = async () => {
    if (!window.confirm('Disconnect the site-wide SelleGard integration? Merchants will lose access to these rates until you reconnect.')) return;
    setBusy('disc');
    try {
      const r = await fetch(`${API}/webship/disconnect`, { method: 'POST', headers: auth });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Failed'); }
      setBanner({ type: 'ok', text: 'Disconnected.' });
      await refresh();
    } catch (e) { setBanner({ type: 'err', text: e.message }); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  const connected = status?.connected;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-sellegard-config">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Settings className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Admin · SelleGard Freight Configuration</h1>
          <p className="text-sm text-slate-500">Site-wide carrier credentials. Every merchant uses this connection automatically.</p>
        </div>
      </div>

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="admin-sg-banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{banner.text}</span>
        </div>
      )}

      {/* Current state */}
      {connected ? (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5" data-testid="sg-current">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-200">Connected · {status.services_count} services</h3>
          </div>
          <dl className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Customer ID</dt>
              <dd className="font-mono">{status.customer_id}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">API key</dt>
              <dd className="font-mono">{status.masked_key}</dd>
            </div>
            {status.integration_ids?.length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wide text-slate-500">Integration IDs</dt>
                <dd>{status.integration_ids.map((i) => i.id).join(', ')}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Last test</dt>
              <dd>{status.last_test_at ? new Date(status.last_test_at).toLocaleString() : '—'}</dd>
            </div>
          </dl>
          <div className="flex gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={doTest} disabled={busy === 'test'} data-testid="test-btn">
              {busy === 'test' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5 mr-1.5" />}
              Test connection
            </Button>
            <Button size="sm" variant="outline" onClick={doDisconnect} disabled={busy === 'disc'} className="text-rose-600" data-testid="disconnect-btn">
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-5 flex items-start gap-3" data-testid="sg-notconnected">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold">Not connected.</p>
            <p className="text-sm text-slate-600 mt-1">Paste the carrier credentials below to enable SelleGard Freight for all merchants.</p>
          </div>
        </div>
      )}

      {/* Connect / rotate form */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="sg-connect-card">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold">{connected ? 'Rotate credentials' : 'Connect carrier'}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Internal use only. These credentials never leave SelleSend and are never exposed to merchants.
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Customer ID"><input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="e.g. 61900068" className="input" data-testid="cust-id-input" /></Field>
          <Field label="API Key"><input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Carrier RSIS token" className="input font-mono" data-testid="api-key-input" /></Field>
          <Field label="Integration ID (optional)"><input value={integrationId} onChange={(e) => setIntegrationId(e.target.value)} placeholder="3214" className="input" data-testid="integ-id-input" /></Field>
        </div>
        <Button onClick={doConnect} disabled={busy === 'connect' || !customerId || !apiKey} className="mt-4 bg-gradient-to-r from-amber-500 to-orange-600" data-testid="admin-connect-btn">
          {busy === 'connect' ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</> : <><Plug className="w-4 h-4 mr-2" />Save &amp; test</>}
        </Button>
      </div>

      <style>{`.input{width:100%;height:38px;padding:0 10px;border-radius:8px;border:1px solid rgb(226 232 240);font-size:13px;background:transparent;}
        .dark .input{border-color:rgb(51 65 85);}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);
